
import { useState, useEffect, useRef } from 'react';
import { useCart } from '../../hooks/useCart';
import { Link } from 'react-router-dom';
import { getProductImage } from '../../utils/imageStorage';

export default function ProduitAngeOrLumiere() {
  const { addItem } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const notificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const [customImage, setCustomImage] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    
    const loadCustomImage = async () => {
      try {
        const image = await getProductImage('ange-or-lumiere');
        if (image && isMountedRef.current) {
          setCustomImage(image);
        }
      } catch (error) {
        console.error('Erreur lors du chargement de l\'image:', error);
      } finally {
        if (isMountedRef.current) {
          setImageLoaded(true);
        }
      }
    };
    
    loadCustomImage();
    
    return () => {
      isMountedRef.current = false;
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
        notificationTimeoutRef.current = null;
      }
    };
  }, []);

  const product = {
    id: 'ange-or-lumiere',
    name: 'Ange en livre plié – Or & Lumière',
    price: 35,
    image: customImage || 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/c3048ea066b5483f60c462226ed92875.png',
    category: 'Collection de Noël',
    subcategory: 'Anges de Noël',
    description: 'Cet ange décoratif est une création artisanale réalisée à partir d\'un livre entièrement plié à la main. Il est sublimé par une tête dorée, des ailes déployées en pages de livre et de délicats motifs dorés qui apportent une touche lumineuse et précieuse à l\'ensemble.'
  };

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    if (!isMountedRef.current) return;
    
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
      notificationTimeoutRef.current = null;
    }

    setNotification({
      show: true,
      message,
      type
    });

    notificationTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setNotification(prev => ({ ...prev, show: false }));
      }
      notificationTimeoutRef.current = null;
    }, 3000);
  };

  const handleAddToCart = () => {
    if (!isMountedRef.current) return;
    
    try {
      for (let i = 0; i < quantity; i++) {
        addItem({
          id: product.id,
          name: product.name,
          price: product.price,
          image: product.image,
          description: product.description
        });
      }

      showNotification(`${quantity} ${product.name} ajouté${quantity > 1 ? 's' : ''} au panier !`, 'success');

    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier:', error);
      showNotification('Erreur lors de l\'ajout au panier', 'error');
    }
  };

  const incrementQuantity = () => setQuantity(prev => prev + 1);
  const decrementQuantity = () => setQuantity(prev => (prev > 1 ? prev - 1 : 1));

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      {/* Breadcrumb */}
      <nav className="bg-[#FAF9F8] py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
            <Link to="/" className="text-[#B97A72] hover:text-[#C49B86] transition-colors">
              Accueil
            </Link>
            <span className="text-[#6B5E5A]">/</span>
            <Link to="/boutique" className="text-[#B97A72] hover:text-[#C49B86] transition-colors">
              Boutique
            </Link>
            <span className="text-[#6B5E5A]">/</span>
            <span className="text-[#6B5E5A]">{product.name}</span>
          </div>
        </div>
      </nav>

      {/* Produit */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Image */}
            <div className="bg-[#FAF9F8] rounded-2xl overflow-hidden border-2 border-[#B97A72] p-8">
              <div className="aspect-square bg-[#F3D9D9] rounded-lg overflow-hidden relative">
                {!imageLoaded ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-loader-4-line text-4xl text-[#B97A72] animate-spin"></i>
                  </div>
                ) : (
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                )}
                <div className="absolute top-4 right-4">
                  <span className="bg-[#C49B86] text-white px-3 py-1 rounded-full text-sm font-medium">
                    Pièce unique ✨
                  </span>
                </div>
              </div>
            </div>

            {/* Détails */}
            <div className="flex flex-col justify-center">
              <span className="text-sm text-[#B97A72] font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                {product.subcategory}
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-[#6B5E5A] mb-4" style={{ fontFamily: 'Allura, cursive' }}>
                {product.name}
              </h1>
              <div className="flex items-baseline space-x-3 mb-6">
                <span className="text-4xl font-bold text-[#B97A72]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {product.price}€
                </span>
              </div>

              <div className="bg-[#FAF9F8] border-2 border-[#B97A72] rounded-lg p-6 mb-6">
                <h2 className="text-xl font-bold text-[#B97A72] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  ✨ Ange en livre plié – Or & Lumière (pièce unique)
                </h2>
                <div className="text-[#6B5E5A] space-y-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <p>
                    Cet ange décoratif est une création artisanale réalisée à partir d'un livre entièrement plié à la main. Il est sublimé par une tête dorée, des ailes déployées en pages de livre et de délicats motifs dorés qui apportent une touche lumineuse et précieuse à l'ensemble.
                  </p>
                  <p>
                    Sa silhouette élancée et ses finitions dorées en font une pièce forte, à la fois douce, spirituelle et élégante, parfaite pour une décoration de Noël raffinée.
                  </p>
                  <p>
                    Chaque création est unique, façonnée avec soin dans mon atelier à partir de livres recyclés. Aucun ange ne peut être reproduit à l'identique.
                  </p>
                  
                  <div className="mt-6">
                    <h3 className="font-semibold text-[#B97A72] mb-2">😇 Idéal pour :</h3>
                    <ul className="space-y-1 ml-4">
                      <li>• Une décoration de Noël chic et lumineuse</li>
                      <li>• Un cadeau artisanal d'exception</li>
                      <li>• Apporter une touche spirituelle et poétique à un intérieur</li>
                    </ul>
                  </div>

                  <div className="mt-6 space-y-1">
                    <p>✅ Création 100 % faite main</p>
                    <p>✅ Pièce unique</p>
                    <p>✅ Livre recyclé – démarche éco-responsable</p>
                    <p>✅ Décorations dorées incluses</p>
                  </div>
                </div>
              </div>

              {/* Quantité */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[#6B5E5A] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Quantité
                </label>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={decrementQuantity}
                    className="w-10 h-10 flex items-center justify-center bg-[#FAF9F8] border-2 border-[#B97A72] rounded-full hover:bg-[#B97A72] hover:text-white transition-colors cursor-pointer"
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                  <span className="text-xl font-semibold text-[#6B5E5A] w-12 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {quantity}
                  </span>
                  <button
                    onClick={incrementQuantity}
                    className="w-10 h-10 flex items-center justify-center bg-[#FAF9F8] border-2 border-[#B97A72] rounded-full hover:bg-[#B97A72] hover:text-white transition-colors cursor-pointer"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
              </div>

              {/* Boutons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleAddToCart}
                  className="flex-1 bg-[#B97A72] text-white px-8 py-4 rounded-full hover:bg-[#C49B86] transition-colors whitespace-nowrap cursor-pointer text-lg font-medium"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  <div className="w-5 h-5 flex items-center justify-center mr-2 inline-block">
                    <i className="ri-shopping-cart-line"></i>
                  </div>
                  Ajouter au panier
                </button>
                <Link
                  to="/boutique"
                  className="flex-1 bg-transparent text-[#B97A72] px-8 py-4 rounded-full border-2 border-[#B97A72] hover:bg-[#B97A72] hover:text-white transition-colors whitespace-nowrap cursor-pointer text-lg font-medium text-center"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Continuer mes achats
                </Link>
              </div>

              {/* Informations supplémentaires */}
              <div className="mt-8 space-y-3">
                <div className="flex items-center text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <div className="w-5 h-5 flex items-center justify-center mr-3">
                    <i className="ri-truck-line text-[#B97A72]"></i>
                  </div>
                  <span>Livraison soignée</span>
                </div>
                <div className="flex items-center text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <div className="w-5 h-5 flex items-center justify-center mr-3">
                    <i className="ri-hand-heart-line text-[#B97A72]"></i>
                  </div>
                  <span>Fait main avec amour</span>
                </div>
                <div className="flex items-center text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <div className="w-5 h-5 flex items-center justify-center mr-3">
                    <i className="ri-recycle-line text-[#B97A72]"></i>
                  </div>
                  <span>Livre recyclé</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notification toast */}
      {notification.show && (
        <div 
          className={`fixed top-20 right-4 z-50 px-6 py-3 rounded-lg shadow-lg transition-all duration-300 ${
            notification.type === 'success' 
              ? 'bg-green-500 text-white' 
              : 'bg-red-500 text-white'
          }`}
          style={{ 
            transform: notification.show ? 'translateX(0)' : 'translateX(100%)',
            opacity: notification.show ? 1 : 0
          }}
        >
          <div className="flex items-center space-x-2">
            <i className={`${notification.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}`}></i>
            <span style={{ fontFamily: 'Poppins, sans-serif' }}>{notification.message}</span>
          </div>
        </div>
      )}
    </div>
  );
}
